import { useCallback, useMemo, useState } from 'react'
import {
  useListHazardIdentifications,
  useCreateHazardIdentification,
  useUpdateHazardIdentification,
  useDeleteHazardIdentification,
  useApproveHazardIdentification,
  useStartHazardGeneration,
  useMachineLocations,
  type HazardGenerationStreamEvent,
  type HazardIdentification,
  CreateHazardIdentificationPayload,
  UpdateHazardIdentificationPayload,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  AlertTriangle,
  ListFilter,
  PlusIcon,
  SparklesIcon,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { AddHazardDialog } from './AddHazardDialog'
import { DeleteHazardDialog } from './DeleteHazardDialog'
import { GenerateFromStandardsModal } from './GenerateFromStandardsModal'
import { filterHazards } from './filterHazards'
import { HazardFiltersSheet } from './HazardFiltersSheet'
import { HazardListCard } from './HazardListCard'
import {
  countActiveHazardListFilters,
  defaultHazardListFilters,
  type HazardListFilters,
} from './hazardListFilters'
import {
  readHazardListFilters,
  saveHazardListFilters,
} from './hazardListFiltersStorage'
import { getHazardMachineLocationLabel } from './getHazardMachineLocationLabel'
import { HazardGenerationSkippedSummaryAlert } from './HazardGenerationSkippedSummaryAlert'
import {
  isHazardUncertainReview,
  resolveKeepUncertainErrorMessage,
} from './hazardUncertainReview'
import { parseHazardGenerationSkippedItems } from './parseHazardGenerationSkippedItems'
import type { HazardGenerationSkippedItem } from './parseHazardGenerationSkippedItems'

export function HazardsPage() {
  const params = useParams<{ machineryDirectiveComplianceId: string }>()
  const machineryDirectiveComplianceId = params.machineryDirectiveComplianceId!
  const { t } = useTranslation(['machineryDirectiveCompliance', 'common'])

  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)
  const [isAddHazardDialogOpen, setIsAddHazardDialogOpen] = useState(false)
  const [editingHazard, setEditingHazard] =
    useState<HazardIdentification | null>(null)
  const [deletingHazardId, setDeletingHazardId] = useState<string | null>(null)
  const [generationError, setGenerationError] = useState<string | null>(null)
  const [progressMessage, setProgressMessage] = useState<string | null>(null)
  const [progressCurrent, setProgressCurrent] = useState<number | null>(null)
  const [progressTotal, setProgressTotal] = useState<number | null>(null)
  const [filtersComplianceId, setFiltersComplianceId] = useState(
    machineryDirectiveComplianceId
  )
  const [hazardListFilters, setHazardListFilters] = useState<HazardListFilters>(
    () =>
      readHazardListFilters(machineryDirectiveComplianceId) ??
      defaultHazardListFilters
  )

  if (filtersComplianceId !== machineryDirectiveComplianceId) {
    setFiltersComplianceId(machineryDirectiveComplianceId)
    setHazardListFilters(
      readHazardListFilters(machineryDirectiveComplianceId) ??
        defaultHazardListFilters
    )
  }

  const handleHazardListFiltersChange = useCallback(
    (filters: HazardListFilters) => {
      setHazardListFilters(filters)
      saveHazardListFilters(machineryDirectiveComplianceId, filters)
    },
    [machineryDirectiveComplianceId]
  )

  const handleClearHazardListFilters = useCallback(() => {
    handleHazardListFiltersChange(defaultHazardListFilters)
  }, [handleHazardListFiltersChange])

  const [isFiltersSheetOpen, setIsFiltersSheetOpen] = useState(false)
  const [skippedSummaryItems, setSkippedSummaryItems] = useState<
    HazardGenerationSkippedItem[]
  >([])

  const {
    data: hazards = [],
    isLoading: isLoadingList,
    error: listError,
  } = useListHazardIdentifications(machineryDirectiveComplianceId)
  const { data: machineLocations = [] } = useMachineLocations(
    machineryDirectiveComplianceId
  )

  const { mutate: startGeneration, isPending: isGenerating } =
    useStartHazardGeneration(machineryDirectiveComplianceId)
  const { mutateAsync: createHazard, isPending: isCreatingHazard } =
    useCreateHazardIdentification(machineryDirectiveComplianceId)
  const { mutateAsync: updateHazard, isPending: isUpdatingHazard } =
    useUpdateHazardIdentification(machineryDirectiveComplianceId)
  const { mutateAsync: deleteHazard, isPending: isDeletingHazard } =
    useDeleteHazardIdentification(machineryDirectiveComplianceId)
  const { mutateAsync: approveHazard, isPending: isApprovingHazard } =
    useApproveHazardIdentification(machineryDirectiveComplianceId)

  const handleOpenGenerateModal = () => {
    setIsGenerateModalOpen(true)
  }

  const handleOpenAddHazard = () => {
    setIsAddHazardDialogOpen(true)
  }

  const handleCreateHazard = async (
    payload: CreateHazardIdentificationPayload
  ) => {
    try {
      await createHazard(payload)
      toast.success(t('hazardsPage.addHazardSuccess'))
      setIsAddHazardDialogOpen(false)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('hazardsPage.addHazardFailed')
      toast.error(message)
    }
  }

  const handleEditHazard = (hazard: HazardIdentification) => {
    setEditingHazard(hazard)
  }

  const handleUpdateHazard = async (
    payload: { hazardId: string } & UpdateHazardIdentificationPayload
  ) => {
    try {
      await updateHazard(payload)
      toast.success(t('actions.saved'))
      setEditingHazard(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('hazardsPage.addHazardFailed')
      toast.error(message)
    }
  }

  const handleDeleteHazard = (hazardId: string) => {
    setDeletingHazardId(hazardId)
  }

  const handleKeepHazard = async (hazardId: string) => {
    try {
      await approveHazard(hazardId)
      toast.success(t('hazardsPage.keepUncertainSuccess'))
    } catch (err: unknown) {
      toast.error(resolveKeepUncertainErrorMessage(err, t))
    }
  }

  const deletingHazard = hazards.find((hazard) => hazard.id === deletingHazardId)
  const isRemovingUncertain = isHazardUncertainReview(deletingHazard?.uncertain)

  const handleConfirmDelete = async () => {
    if (deletingHazardId == null) {
      return
    }
    try {
      await deleteHazard(deletingHazardId)
      toast.success(
        isRemovingUncertain
          ? t('hazardsPage.removeUncertainSuccess')
          : t('hazardsPage.hazardDeletedSuccess')
      )
      setDeletingHazardId(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : isRemovingUncertain
            ? t('hazardsPage.removeUncertainFailed')
            : t('hazardsPage.deleteHazardFailed')
      toast.error(message)
    }
  }

  const isMutationPending =
    isCreatingHazard ||
    isUpdatingHazard ||
    isDeletingHazard ||
    isApprovingHazard

  const handleGenerateFromStandardsConfirm = (selectedHazardIds: string[]) => {
    setGenerationError(null)
    setProgressMessage(null)
    setProgressCurrent(null)
    setProgressTotal(null)
    setSkippedSummaryItems([])
    startGeneration(
      {
        selectedHazardIds,
        onEvent: (event: HazardGenerationStreamEvent) => {
          switch (event.type) {
            case 'progress':
              setProgressMessage(event.message)
              break
            case 'hazard.detected':
              setProgressCurrent(event.hazardIndex)
              setProgressTotal(event.totalHazards)
              setProgressMessage(null)
              break
            case 'hazard.created':
              setProgressCurrent(event.hazardIndex)
              setProgressTotal(event.totalHazards)
              break
            case 'workflow.completed': {
              const skipped = parseHazardGenerationSkippedItems(event.result)
              if (skipped.length > 0) {
                setSkippedSummaryItems(skipped)
              }
              break
            }
            case 'step.started':
              if (event.step === 'collect_standards_hazards') {
                setProgressMessage(t('hazardsPage.generating'))
              }
              break
            default:
              break
          }
        },
        onComplete: () => {
          setProgressMessage(null)
          setProgressCurrent(null)
          setProgressTotal(null)
          toast.success(t('hazardsPage.successToast'))
        },
      },
      {
        onError: (err) => {
          setProgressMessage(null)
          setProgressCurrent(null)
          setProgressTotal(null)
          setGenerationError(err.message)
          toast.error(t('hazardsPage.generateFailed'))
        },
      }
    )
  }

  const listDisplayError =
    listError instanceof Error ? listError.message : null
  const displayError = generationError ?? listDisplayError

  const generatedSignificantHazardIds = useMemo(() => {
    const set = new Set<string>()
    for (const h of hazards) {
      if (
        h.source === 'AGENT' &&
        h.significantHazardId != null &&
        h.significantHazardId !== ''
      ) {
        set.add(h.significantHazardId)
      }
    }
    return set
  }, [hazards])

  const machineLocationsById = useMemo(
    () => new Map(machineLocations.map((location) => [location.id, location])),
    [machineLocations]
  )

  const uncertainHazards = useMemo(
    () => hazards.filter((hazard) => isHazardUncertainReview(hazard.uncertain)),
    [hazards]
  )

  const confirmedHazards = useMemo(
    () =>
      hazards.filter((hazard) => !isHazardUncertainReview(hazard.uncertain)),
    [hazards]
  )

  const hasUncertainHazards = uncertainHazards.length > 0

  const filteredHazards = useMemo(
    () => filterHazards(hazards, hazardListFilters),
    [hazards, hazardListFilters]
  )

  const filteredConfirmedHazards = useMemo(
    () => filterHazards(confirmedHazards, hazardListFilters),
    [confirmedHazards, hazardListFilters]
  )

  const filteredUncertainHazards = useMemo(
    () => filterHazards(uncertainHazards, hazardListFilters),
    [uncertainHazards, hazardListFilters]
  )

  const activeFilterCount = countActiveHazardListFilters(hazardListFilters)

  const renderHazardCards = (hazardList: HazardIdentification[]) => (
    <div className='min-w-0 space-y-3'>
      {hazardList.map((hazard) => (
        <HazardListCard
          key={hazard.id}
          hazard={hazard}
          locationLabel={getHazardMachineLocationLabel(
            hazard,
            machineLocationsById,
            t
          )}
          onEdit={handleEditHazard}
          onDelete={handleDeleteHazard}
          onKeep={handleKeepHazard}
          isMutationPending={isMutationPending}
        />
      ))}
    </div>
  )

  const renderFilterNoResults = () => (
    <Empty className='min-h-[200px] rounded-lg border border-dashed'>
      <EmptyHeader>
        <EmptyTitle>{t('hazardsPage.filterNoResultsTitle')}</EmptyTitle>
        <EmptyDescription>
          {t('hazardsPage.filterNoResultsDescription')}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  )

  if (machineryDirectiveComplianceId == null) {
    return null
  }

  if (isLoadingList) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      <ComplianceSubPageHeader className='flex-col items-start sm:flex-col sm:items-start'>
        <ComplianceSubPageTitle>{t('nav.hazards')}</ComplianceSubPageTitle>
        {listDisplayError == null && (
          <div className='flex w-full flex-wrap items-center justify-between gap-2'>
            <div className='flex flex-wrap gap-2'>
              <Button
                onClick={handleOpenAddHazard}
                disabled={isCreatingHazard || isGenerating}
              >
                <PlusIcon className='mr-2 size-4' />
                {t('hazardsPage.addHazard')}
              </Button>
              {hazards.length > 0 && !isGenerating && (
                <Button
                  onClick={handleOpenGenerateModal}
                  variant='outline'
                  disabled={isGenerating}
                >
                  <SparklesIcon className='mr-2 size-4' />
                  {t('hazardsPage.generateFromStandards')}
                </Button>
              )}
            </div>
            {hazards.length > 0 && (
              <div className='flex flex-wrap gap-2'>
                <Button
                  variant='outline'
                  onClick={() => setIsFiltersSheetOpen(true)}
                >
                  <ListFilter className='mr-2 size-4' />
                  {t('hazardsPage.filtersButton')}
                  {activeFilterCount > 0 && (
                    <Badge variant='secondary' className='ml-2'>
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
                {activeFilterCount > 0 && (
                  <Button
                    variant='outline'
                    onClick={handleClearHazardListFilters}
                  >
                    <Trash2 className='mr-2 size-4' />
                    {t('common:dataTable.clearFilters')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </ComplianceSubPageHeader>

      {displayError != null && (
        <Alert variant='destructive'>
          <AlertTitle>{t('hazardsPage.errorTitle')}</AlertTitle>
          <AlertDescription>{displayError}</AlertDescription>
        </Alert>
      )}

      {isGenerating && (
        <Alert className='border-primary/50 bg-primary/5'>
          <AlertTriangle className='size-4' />
          <AlertTitle>{t('hazardsPage.generating')}</AlertTitle>
          <AlertDescription>
            {progressCurrent != null && progressTotal != null
              ? t('hazardsPage.generatingHazard', {
                  current: progressCurrent,
                  total: progressTotal,
                })
              : (progressMessage ?? t('hazardsPage.generating'))}
          </AlertDescription>
        </Alert>
      )}

      {!isGenerating && skippedSummaryItems.length > 0 && (
        <HazardGenerationSkippedSummaryAlert
          skippedItems={skippedSummaryItems}
          onDismiss={() => setSkippedSummaryItems([])}
        />
      )}

      {!displayError && hazards.length === 0 && !isGenerating && (
        <Empty className='min-h-[320px] rounded-lg border border-dashed'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <AlertTriangle className='size-10' />
            </EmptyMedia>
            <EmptyTitle>{t('hazardsPage.title')}</EmptyTitle>
            <EmptyDescription>
              {t('hazardsPage.emptyStateMessage')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className='flex flex-wrap justify-center gap-2'>
              <Button
                onClick={handleOpenAddHazard}
                disabled={isCreatingHazard}
                size='sm'
                className='gap-2'
              >
                {t('hazardsPage.addHazard')}
              </Button>
              <Button
                onClick={handleOpenGenerateModal}
                variant='outline'
                disabled={isGenerating}
                size='sm'
                className='gap-2'
              >
                <AlertTriangle className='size-4' />
                {t('hazardsPage.generateFromStandards')}
              </Button>
            </div>
          </EmptyContent>
        </Empty>
      )}

      {!displayError &&
        hazards.length > 0 &&
        !hasUncertainHazards &&
        filteredHazards.length === 0 &&
        renderFilterNoResults()}

      {!displayError &&
        hazards.length > 0 &&
        !hasUncertainHazards &&
        filteredHazards.length > 0 &&
        renderHazardCards(filteredHazards)}

      {!displayError && hazards.length > 0 && hasUncertainHazards && (
        <Tabs defaultValue='needsReview' className='mt-2 w-full'>
          <TabsList>
            <TabsTrigger value='hazards'>
              {t('hazardsPage.hazardsTab')} ({filteredConfirmedHazards.length})
            </TabsTrigger>
            <TabsTrigger value='needsReview'>
              {t('hazardsPage.needsReviewTab')} ({filteredUncertainHazards.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value='hazards' className='mt-4'>
            {filteredConfirmedHazards.length > 0
              ? renderHazardCards(filteredConfirmedHazards)
              : activeFilterCount > 0
                ? renderFilterNoResults()
                : (
                    <p className='py-6 text-center text-muted-foreground'>
                      {t('hazardsPage.confirmedHazardsEmpty')}
                    </p>
                  )}
          </TabsContent>
          <TabsContent value='needsReview' className='mt-4'>
            {filteredUncertainHazards.length > 0
              ? renderHazardCards(filteredUncertainHazards)
              : renderFilterNoResults()}
          </TabsContent>
        </Tabs>
      )}

      <AddHazardDialog
        complianceId={machineryDirectiveComplianceId}
        isOpen={isAddHazardDialogOpen}
        onClose={() => setIsAddHazardDialogOpen(false)}
        onCreate={handleCreateHazard}
        isCreating={isCreatingHazard}
      />

      <AddHazardDialog
        complianceId={machineryDirectiveComplianceId}
        key={editingHazard?.id ?? 'edit-dialog'}
        isOpen={editingHazard != null}
        onClose={() => setEditingHazard(null)}
        hazard={editingHazard ?? undefined}
        onCreate={() => {}}
        onUpdate={handleUpdateHazard}
        isUpdating={isUpdatingHazard}
      />

      <DeleteHazardDialog
        isOpen={deletingHazardId != null}
        onClose={() => setDeletingHazardId(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeletingHazard}
        copy={isRemovingUncertain ? 'remove' : 'delete'}
      />

      <HazardFiltersSheet
        filters={hazardListFilters}
        onFiltersChange={handleHazardListFiltersChange}
        open={isFiltersSheetOpen}
        onOpenChange={setIsFiltersSheetOpen}
        machineLocations={machineLocations}
      />

      <GenerateFromStandardsModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        complianceId={machineryDirectiveComplianceId}
        onGenerate={handleGenerateFromStandardsConfirm}
        isGenerating={isGenerating}
        generatedSignificantHazardIds={generatedSignificantHazardIds}
      />
    </div>
  )
}
